import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, CircularProgress } from '@mui/material';

import SleeperProfilePage from './SleeperProfilePage';
import {
  Rating,
  RatingReply,
  CommissionerRating,
  SleeperProfile,
  RatingStats,
  CommissionerRatingStats,
  SleeperAccount,
  League,
  UsernameHistory,
} from '../types';

/**
 * Design preview for the public Sleeper profile page.
 *
 * Renders the real `SleeperProfilePage` component but seeds React
 * Query's cache with hypothetical data first, so the page paints fully
 * populated without making any network calls. Any change to the real
 * page's layout/styling is reflected here automatically — there is no
 * duplicated JSX to maintain.
 *
 * Use this to iterate on the look of the profile when it's full of
 * data (lots of leagues across years, many manager + commissioner
 * reviews, replies, anonymous reviews, etc.) without depending on a
 * real Sleeper account or production-shaped DB rows.
 *
 * Mounted at `/preview/profile`. Not linked from the nav — bookmark or
 * paste the URL directly. Fully client-side; no special routing or
 * env-flag protection is required, but treat it as a developer tool.
 */

const PREVIEW_USER_ID = '__preview__';
const PREVIEW_ACCOUNT_ID = '00000000-0000-4000-8000-000000000001';

export default function SleeperProfilePagePreview() {
  const queryClient = useQueryClient();
  // We only render the real page after the cache is seeded; otherwise
  // its `useQuery` hooks would fire a real network request first and
  // briefly flash a loader (or, worse, error out) before our seed lands.
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    queryClient.setQueryData<SleeperProfile>(
      ['sleeperProfile', PREVIEW_USER_ID],
      MOCK_PROFILE
    );
    queryClient.setQueryData<{ ratings: Rating[]; stats: RatingStats }>(
      ['accountRatings', PREVIEW_ACCOUNT_ID],
      MOCK_ACCOUNT_RATINGS
    );
    queryClient.setQueryData<{
      ratings: CommissionerRating[];
      stats: CommissionerRatingStats | null;
    }>(['commissionerRatings', PREVIEW_ACCOUNT_ID], MOCK_COMMISH_RATINGS);
    setSeeded(true);
  }, [queryClient]);

  if (!seeded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return <SleeperProfilePage sleeperUserIdOverride={PREVIEW_USER_ID} />;
}

// ─── Mock data ───────────────────────────────────────────────────────
//
// Adjust freely. The shapes here mirror the wire format the real
// /api/users/sleeper/:id endpoint returns. Avatar IDs that don't exist
// on Sleeper's CDN simply 404 and fall back to the initial-letter
// avatar — that's intentional, no need to find real Sleeper avatars.

const MOCK_ACCOUNT: SleeperAccount = {
  id: PREVIEW_ACCOUNT_ID,
  user_id: 'preview-user-uid',
  sleeper_user_id: PREVIEW_USER_ID,
  current_username: 'DynastyKing24',
  current_display_name: 'Marcus Chen',
  current_avatar_id: null,
  is_verified: true,
  verification_code: null,
  created_at: '2023-08-15T10:30:00.000Z',
  last_synced_at: '2026-04-24T15:00:00.000Z',
};

const MOCK_USERNAME_HISTORY: UsernameHistory[] = [
  {
    id: 'h1',
    username: 'DynastyKing24',
    display_name: 'Marcus Chen',
    first_seen_at: '2024-01-01T00:00:00.000Z',
    last_seen_at: '2026-04-24T00:00:00.000Z',
  },
  {
    id: 'h2',
    username: 'KingChen2022',
    display_name: 'M. Chen',
    first_seen_at: '2022-03-15T00:00:00.000Z',
    last_seen_at: '2024-01-01T00:00:00.000Z',
  },
  {
    id: 'h3',
    username: 'ChenIsBoss',
    display_name: 'Marcus',
    first_seen_at: '2020-09-01T00:00:00.000Z',
    last_seen_at: '2022-03-15T00:00:00.000Z',
  },
];

// Tuned to demo the three score tiers side by side:
//   - Manager Trust Score 4.8 → "elite" gold halo (slow pulse).
//   - Commissioner Score 5.0 → "electric" legendary crackle (fast,
//     with bright white spark flashes).
// Drop these below 4.8 to see the flat non-elite state.
const MOCK_RATING_STATS: RatingStats = {
  total_ratings: 24,
  average_payment_reliability: 4.9,
  average_communication: 4.7,
  average_fair_play: 4.8,
  average_league_commitment: 4.9,
  average_fair_trader: 4.7,
  overall_average: 4.8,
};

const MOCK_COMMISH_STATS: CommissionerRatingStats = {
  total_ratings: 8,
  average_fairness: 5.0,
  average_rule_enforcement: 5.0,
  average_responsiveness: 5.0,
  average_communication: 4.9,
  average_organization: 5.0,
  overall_average: 5.0,
};

// Helper to keep the league list readable.
function league(
  id: string,
  name: string,
  season: string,
  status: League['status'],
  total_rosters = 12
): League {
  return {
    id,
    sleeper_league_id: `sleeper-${id}`,
    name,
    season,
    sport: 'nfl',
    status,
    total_rosters,
    avatar_id: null,
    created_at: `${season}-08-01T00:00:00.000Z`,
    last_synced_at: '2026-04-24T15:00:00.000Z',
  };
}

const MOCK_LEAGUES: League[] = [
  // Current season (in-season + pre-draft).
  league('l1', 'Empire of Dynasty', '2026', 'in_season', 12),
  league('l2', 'BBQ Boys Dynasty', '2026', 'in_season', 10),
  league('l3', 'Startup 2026', '2026', 'pre_draft', 14),

  // 2025 — three completed leagues.
  league('l4', 'Empire of Dynasty', '2025', 'complete', 12),
  league('l5', 'BBQ Boys Dynasty', '2025', 'complete', 10),
  league('l6', 'Touchdown Titans', '2025', 'complete', 12),

  // 2024 — four completed leagues.
  league('l7', 'Empire of Dynasty', '2024', 'complete', 12),
  league('l8', 'BBQ Boys Dynasty', '2024', 'complete', 10),
  league('l9', 'Friday Night Footballers', '2024', 'complete', 14),
  league('l10', 'The Iron League', '2024', 'complete', 12),

  // 2023 — three completed leagues.
  league('l11', 'Empire of Dynasty', '2023', 'complete', 12),
  league('l12', 'BBQ Boys Dynasty', '2023', 'complete', 10),
  league('l13', 'Old School Dynasty', '2023', 'complete', 12),

  // 2022 — two completed.
  league('l14', 'Empire of Dynasty', '2022', 'complete', 12),
  league('l15', 'College Buddies', '2022', 'complete', 10),

  // 2021 — single league, the start of his dynasty career.
  league('l16', 'College Buddies', '2021', 'complete', 10),
];

// Toggle to demo the populated commissioner state. Default `false`
// shows the new blurred-skeleton fallback in the Commissioner Score
// card and drops the Commissioner tab from the reviews section.
// Typed as `boolean` (not a literal) so TypeScript exercises both
// branches of the consuming ternaries below.
const SHOW_COMMISH_DATA: boolean = false;

const MOCK_PROFILE: SleeperProfile = {
  account: MOCK_ACCOUNT,
  usernameHistory: MOCK_USERNAME_HISTORY,
  avatarHistory: [],
  ratingStats: MOCK_RATING_STATS,
  commissionerRatingStats: SHOW_COMMISH_DATA ? MOCK_COMMISH_STATS : null,
  leagues: MOCK_LEAGUES,
  // Set to true so the rate buttons render in the preview. Toggle to
  // false to see the alert-state version of the right rail.
  canRate: true,
  cannotRateReason: undefined,
  sharedLeagues: ['l4'],
  canRateAsCommissioner: true,
  cannotRateCommissionerReason: undefined,
  sharedCommishLeagues: [
    { id: 'l4', name: 'Empire of Dynasty', season: '2025' },
  ],
  hasSharedIpWarning: false,
};

// Helper to assemble a rater object so reviews stay readable below.
function rater(
  odId: string,
  username: string,
  displayName: string | null,
  isVerified = true,
  avatarId: string | null = null
) {
  return {
    odId,
    sleeperUsername: username,
    sleeperDisplayName: displayName,
    sleeperAvatarId: avatarId,
    isVerified,
  };
}

function reply(
  id: string,
  ratingId: string,
  authorUsername: string,
  text: string,
  daysAgo: number
): RatingReply {
  return {
    id,
    rating_id: ratingId,
    author_id: `${authorUsername}-uid`,
    reply_text: text,
    created_at: new Date(Date.now() - daysAgo * 86400_000).toISOString(),
    updated_at: new Date(Date.now() - daysAgo * 86400_000).toISOString(),
    author: rater(`${authorUsername}-uid`, authorUsername, null, true),
  };
}

const MOCK_REVIEWS: Rating[] = [
  {
    id: 'rev1',
    rater_id: 'rater1-uid',
    rated_sleeper_account_id: PREVIEW_ACCOUNT_ID,
    league_id: 'l4',
    payment_reliability: 5,
    communication: 5,
    fair_play: 5,
    league_commitment: 5,
    fair_trader: 5,
    review_text:
      "Marcus runs the cleanest league I've been in. Always pays on time, responds in the chat in minutes, and his trades are always thoughtful. 10/10 would draft against again.",
    is_anonymous: false,
    created_at: '2026-02-10T14:30:00.000Z',
    updated_at: '2026-02-10T14:30:00.000Z',
    isVerifiedLeaguemate: true,
    rater: rater('rater1-uid', 'DraftKingMike', 'Mike R.', true),
    replies: [
      reply(
        'rep1',
        'rev1',
        'DynastyKing24',
        "Appreciate it Mike! Looking forward to another year with you in the league.",
        12
      ),
    ],
  },
  {
    id: 'rev2',
    rater_id: 'rater2-uid',
    rated_sleeper_account_id: PREVIEW_ACCOUNT_ID,
    league_id: 'l4',
    payment_reliability: 5,
    communication: 4,
    fair_play: 5,
    league_commitment: 5,
    fair_trader: 4,
    review_text:
      "Solid league member. Trades fair, pays the buy-in same day. Sometimes slow to respond on weekends but nothing crazy.",
    is_anonymous: false,
    created_at: '2026-01-22T09:15:00.000Z',
    updated_at: '2026-01-22T09:15:00.000Z',
    isVerifiedLeaguemate: true,
    rater: rater('rater2-uid', 'TradeQueen', 'Sarah L.', true),
    replies: [],
  },
  {
    id: 'rev3',
    rater_id: 'rater3-uid',
    rated_sleeper_account_id: PREVIEW_ACCOUNT_ID,
    league_id: 'l5',
    payment_reliability: 5,
    communication: 5,
    fair_play: 5,
    league_commitment: 5,
    fair_trader: 5,
    review_text:
      "Been in BBQ Boys with Marcus for 4 years now. He's the glue holding the league together honestly.",
    is_anonymous: false,
    created_at: '2026-01-05T18:45:00.000Z',
    updated_at: '2026-01-05T18:45:00.000Z',
    isVerifiedLeaguemate: true,
    rater: rater('rater3-uid', 'GridironGuru', 'Tom F.', true),
    replies: [
      reply(
        'rep2',
        'rev3',
        'DynastyKing24',
        "Couldn't run it without you guys.",
        45
      ),
      reply(
        'rep3',
        'rev3',
        'TradeQueen',
        "+1 to all of this. Best league experience I've had.",
        44
      ),
    ],
  },
  {
    id: 'rev4',
    rater_id: 'rater4-uid',
    rated_sleeper_account_id: PREVIEW_ACCOUNT_ID,
    league_id: 'l5',
    payment_reliability: 5,
    communication: 4,
    fair_play: 4,
    league_commitment: 5,
    fair_trader: 5,
    review_text: null,
    is_anonymous: true,
    created_at: '2025-12-15T22:00:00.000Z',
    updated_at: '2025-12-15T22:00:00.000Z',
    isVerifiedLeaguemate: true,
    rater: null,
    replies: [],
  },
  {
    id: 'rev5',
    rater_id: 'rater5-uid',
    rated_sleeper_account_id: PREVIEW_ACCOUNT_ID,
    league_id: 'l7',
    payment_reliability: 5,
    communication: 5,
    fair_play: 5,
    league_commitment: 5,
    fair_trader: 5,
    review_text:
      "Fair trader, knows the value charts but also makes deals that work for everyone. Good guy.",
    is_anonymous: false,
    created_at: '2025-11-08T12:20:00.000Z',
    updated_at: '2025-11-08T12:20:00.000Z',
    isVerifiedLeaguemate: true,
    rater: rater('rater5-uid', 'WaiverWizard', 'Alex P.', true),
    replies: [],
  },
  {
    id: 'rev6',
    rater_id: 'rater6-uid',
    rated_sleeper_account_id: PREVIEW_ACCOUNT_ID,
    league_id: 'l8',
    payment_reliability: 4,
    communication: 4,
    fair_play: 4,
    league_commitment: 4,
    fair_trader: 3,
    review_text:
      "Tough trade negotiator. Not a bad thing — just expect to get the short end if you don't know your values.",
    is_anonymous: false,
    created_at: '2025-09-30T16:00:00.000Z',
    updated_at: '2025-09-30T16:00:00.000Z',
    isVerifiedLeaguemate: true,
    rater: rater('rater6-uid', 'RookieScout', 'Jake D.', false),
    replies: [
      reply(
        'rep4',
        'rev6',
        'DynastyKing24',
        "Fair feedback! Always happy to walk through valuations if you ever want to chat.",
        205
      ),
    ],
  },
  {
    id: 'rev7',
    rater_id: 'rater7-uid',
    rated_sleeper_account_id: PREVIEW_ACCOUNT_ID,
    league_id: 'l9',
    payment_reliability: 5,
    communication: 5,
    fair_play: 5,
    league_commitment: 5,
    fair_trader: 4,
    review_text: null,
    is_anonymous: false,
    created_at: '2025-08-12T20:30:00.000Z',
    updated_at: '2025-08-12T20:30:00.000Z',
    isVerifiedLeaguemate: true,
    rater: rater('rater7-uid', 'DynastyDad', 'Chris W.', true),
    replies: [],
  },
  {
    id: 'rev8',
    rater_id: 'rater8-uid',
    rated_sleeper_account_id: PREVIEW_ACCOUNT_ID,
    league_id: 'l11',
    payment_reliability: 5,
    communication: 5,
    fair_play: 5,
    league_commitment: 5,
    fair_trader: 5,
    review_text:
      "Bought into a league mid-season as a replacement and Marcus walked me through everything. Class act.",
    is_anonymous: false,
    created_at: '2024-10-20T11:00:00.000Z',
    updated_at: '2024-10-20T11:00:00.000Z',
    isVerifiedLeaguemate: false,
    rater: rater('rater8-uid', 'NewToDynasty', 'Pat M.', false),
    replies: [],
  },
];

const MOCK_ACCOUNT_RATINGS = {
  ratings: MOCK_REVIEWS,
  stats: MOCK_RATING_STATS,
};

const MOCK_COMMISH_REVIEWS: CommissionerRating[] = [
  {
    id: 'crev1',
    rater_id: 'rater1-uid',
    rated_sleeper_account_id: PREVIEW_ACCOUNT_ID,
    league_id: 'l4',
    fairness: 5,
    rule_enforcement: 5,
    responsiveness: 5,
    communication: 5,
    organization: 5,
    review_text:
      "Best commissioner I've had. Sets clear rules at the start, applies them consistently, and never plays favorites even when his own team is involved.",
    is_anonymous: false,
    is_visible: true,
    created_at: '2026-02-12T10:00:00.000Z',
    updated_at: '2026-02-12T10:00:00.000Z',
    league: { id: 'l4', name: 'Empire of Dynasty', season: '2025' },
    rater: rater('rater1-uid', 'DraftKingMike', 'Mike R.', true),
  },
  {
    id: 'crev2',
    rater_id: 'rater3-uid',
    rated_sleeper_account_id: PREVIEW_ACCOUNT_ID,
    league_id: 'l5',
    fairness: 5,
    rule_enforcement: 4,
    responsiveness: 5,
    communication: 5,
    organization: 5,
    review_text:
      "Always responds in the chat within an hour. Has a clear process for trade vetoes and explains his reasoning when he uses it. Great commish.",
    is_anonymous: false,
    is_visible: true,
    created_at: '2026-01-25T14:30:00.000Z',
    updated_at: '2026-01-25T14:30:00.000Z',
    league: { id: 'l5', name: 'BBQ Boys Dynasty', season: '2025' },
    rater: rater('rater3-uid', 'GridironGuru', 'Tom F.', true),
  },
  {
    id: 'crev3',
    rater_id: 'rater9-uid',
    rated_sleeper_account_id: PREVIEW_ACCOUNT_ID,
    league_id: 'l7',
    fairness: 5,
    rule_enforcement: 5,
    responsiveness: 4,
    communication: 4,
    organization: 5,
    review_text: null,
    is_anonymous: true,
    is_visible: true,
    created_at: '2025-12-01T09:00:00.000Z',
    updated_at: '2025-12-01T09:00:00.000Z',
    league: { id: 'l7', name: 'Empire of Dynasty', season: '2024' },
    rater: null,
  },
];

// Mirror the same SHOW_COMMISH_DATA toggle for the reviews list so
// the page reads consistently in either state.
const MOCK_COMMISH_RATINGS: {
  ratings: CommissionerRating[];
  stats: CommissionerRatingStats | null;
} = SHOW_COMMISH_DATA
  ? { ratings: MOCK_COMMISH_REVIEWS, stats: MOCK_COMMISH_STATS }
  : { ratings: [], stats: null };
